/**
 * Universal Table Schema Parser
 * Generates table column configurations from any OpenAPI schema
 */

export interface TableColumnConfig {
  key: string;
  label: string;
  type: 'text' | 'badge' | 'date' | 'datetime' | 'email' | 'phone' | 'status' | 'actions';
  sortable?: boolean;
  filterable?: boolean;
  width?: string;
  align?: 'left' | 'center' | 'right';
  render?: (value: any, row: any) => React.ReactNode;
  badgeVariant?: (value: any) => string;
  statusType?: 'certificate' | 'deployment' | 'environment';
}

interface OpenAPIProperty {
  type: string;
  description?: string;
  example?: string;
  enum?: string[];
  format?: string;
  pattern?: string;
  items?: {
    type: string;
    enum?: string[];
  };
}

interface OpenAPISchema {
  type: string;
  required?: string[];
  properties: Record<string, OpenAPIProperty>;
}

/**
 * Maps OpenAPI property types to table column types
 */
function mapOpenAPITypeToColumnType(property: OpenAPIProperty, fieldName: string): TableColumnConfig['type'] {
  // Handle specific field names that should be badges
  if (fieldName.toLowerCase().includes('status')) {
    return 'status';
  }
  
  if (fieldName.toLowerCase().includes('environment')) {
    return 'badge';
  }
  
  if (fieldName.toLowerCase().includes('type')) {
    return 'badge';
  }

  // Handle specific formats
  if (property.format) {
    switch (property.format) {
      case 'email':
        return 'email';
      case 'date-time':
        return 'datetime';
      case 'date':
        return 'date';
      case 'tel':
      case 'phone':
        return 'phone';
      default:
        break;
    }
  }

  // Handle date fields by name
  if (fieldName.toLowerCase().includes('date') || 
      fieldName.toLowerCase().includes('at') ||
      fieldName.toLowerCase().includes('time')) {
    return 'datetime';
  }

  // Handle enums as badges
  if (property.enum) {
    return 'badge';
  }

  // Default to text
  return 'text';
}

/**
 * Generates user-friendly labels from field names
 */
function generateColumnLabel(fieldName: string): string {
  // Common field name mappings
  const labelMap: Record<string, string> = {
    // Certificate fields
    commonName: 'Common Name',
    certificateId: 'Certificate ID',
    certificateType: 'Type',
    serialNumber: 'Serial Number',
    issuedAt: 'Issued',
    expiresAt: 'Expires',
    createdAt: 'Created',
    updatedAt: 'Updated',
    createdBy: 'Created By',
    updatedBy: 'Updated By',
    revokedBy: 'Revoked By',
    revokedAt: 'Revoked',
    
    // Organization fields
    organizationName: 'Organization',
    organizationId: 'Organization ID',
    
    // Product fields
    productName: 'Product',
    productId: 'Product ID',
    
    // Environment fields
    environmentName: 'Environment',
    environmentId: 'Environment ID',
    environmentType: 'Environment Type',
    
    // Common fields
    name: 'Name',
    email: 'Email',
    contactNumber: 'Phone',
    notificationEmail: 'Notification Email',
    description: 'Description',
    status: 'Status',
    environment: 'Environment'
  };

  if (labelMap[fieldName]) {
    return labelMap[fieldName];
  }

  // Generate from field name as fallback
  return fieldName
    .replace(/([A-Z])/g, ' $1') // Add space before capitals
    .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
    .replace(/\s+/g, ' ') // Clean up multiple spaces
    .trim();
}

/**
 * Determines if a field should be hidden from tables
 */
function shouldHideField(fieldName: string): boolean {
  const hiddenFields = [
    'id', 'version', 'lastModified', 'internalId',
    'keyUsage', 'extendedKeyUsage', // Complex arrays not suitable for table display
    'subjectAlternativeNames', // Complex data better shown in details
    'organizationalUnit', 'locality', 'state', 'country' // Too much detail for table
  ];
  return hiddenFields.includes(fieldName);
}

/**
 * Gets badge variant for different field types
 */
function getBadgeVariant(fieldName: string, value: any): string {
  // Status badges
  if (fieldName.toLowerCase().includes('status')) {
    switch (value?.toLowerCase()) {
      case 'active': return 'success';
      case 'inactive': return 'secondary';
      case 'pending': return 'warning';
      case 'expired': return 'warning';
      case 'revoked': return 'danger';
      case 'failed': return 'danger';
      default: return 'secondary';
    }
  }
  
  // Environment badges
  if (fieldName.toLowerCase().includes('environment')) {
    return value === 'prod' ? 'danger' : 'info';
  }
  
  // Certificate type badges
  if (fieldName === 'certificateType') {
    return value === 'client' ? 'info' : 'warning';
  }
  
  return 'secondary';
}

/**
 * Universal function to parse any OpenAPI schema into table column configurations
 */
export function parseOpenAPISchemaToColumns(
  openApiSpec: any,
  schemaName: string,
  hiddenFields: string[] = [],
  customLabels: Record<string, string> = {},
  customColumnTypes: Record<string, TableColumnConfig['type']> = {}
): TableColumnConfig[] {
  try {
    if (!openApiSpec || !openApiSpec.components || !openApiSpec.components.schemas) {
      console.error('Invalid OpenAPI specification provided');
      return [];
    }

    const schemas = openApiSpec.components.schemas;
    const schema = schemas[schemaName] as OpenAPISchema;
    
    if (!schema || !schema.properties) {
      console.error(`Schema ${schemaName} not found or invalid in OpenAPI spec`);
      console.log('Available schemas:', Object.keys(schemas));
      return [];
    }

    const columnConfigs: TableColumnConfig[] = [];

    // Process each property in the schema
    Object.entries(schema.properties).forEach(([fieldName, property]) => {
      // Skip fields that should be hidden
      if (hiddenFields.includes(fieldName) || shouldHideField(fieldName)) {
        return;
      }

      const columnType = customColumnTypes[fieldName] || mapOpenAPITypeToColumnType(property, fieldName);
      
      const config: TableColumnConfig = {
        key: fieldName,
        label: customLabels[fieldName] || generateColumnLabel(fieldName),
        type: columnType,
        sortable: true, // Most fields are sortable
        filterable: columnType === 'badge' || columnType === 'status', // Enum-like fields are filterable
      };

      // Add badge variant function for badge columns
      if (columnType === 'badge' || columnType === 'status') {
        config.badgeVariant = (value: any) => getBadgeVariant(fieldName, value);
      }

      // Set status type for status columns
      if (columnType === 'status') {
        if (fieldName.toLowerCase().includes('certificate')) {
          config.statusType = 'certificate';
        } else if (fieldName.toLowerCase().includes('deployment')) {
          config.statusType = 'deployment';
        } else if (fieldName.toLowerCase().includes('environment')) {
          config.statusType = 'environment';
        }
      }

      // Set alignment for different column types
      if (columnType === 'date' || columnType === 'datetime') {
        config.align = 'center';
      }

      columnConfigs.push(config);
    });

    // Sort columns for better UX (important fields first)
    return columnConfigs.sort((a, b) => {
      const importantFields = ['name', 'commonName', 'title', 'email'];
      const aImportant = importantFields.some(field => a.key.toLowerCase().includes(field));
      const bImportant = importantFields.some(field => b.key.toLowerCase().includes(field));
      
      if (aImportant && !bImportant) return -1;
      if (bImportant && !aImportant) return 1;
      
      return 0;
    });

  } catch (error) {
    console.error('Error parsing OpenAPI schema for table:', error);
    return [];
  }
}

/**
 * Gets available schemas from any OpenAPI spec
 */
export function getAvailableSchemas(openApiSpec: any): string[] {
  try {
    if (!openApiSpec || !openApiSpec.components || !openApiSpec.components.schemas) {
      return [];
    }
    return Object.keys(openApiSpec.components.schemas);
  } catch (error) {
    console.error('Error getting available schemas:', error);
    return [];
  }
}